import { useCompanyContext } from "@/pages/company/use-company-context";
import { actingAsDeclaration } from "@/lib/share-item";
import { projectWriteErrorMessage } from "@/lib/project-write-error";
import { useState } from "react";
import { useMutation } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router";
import { throwIfValidationRejected, useValidationFallback } from "@/components/match/use-match-editor";
import { CreateProjectDocument } from "@/graphql/generated/operations";
import { DashboardCreatePage } from "./DashboardCreatePage";
import { ProjectDashboard } from "./ProjectDashboard";

// Ein Projekt der Art PROJECT: mit Id in der Adresse sein Dashboard, ohne Id (SHARE · Erstellen)
// die Anlegen-Maske. Der aktive Unternehmenskontext bestimmt den Besitz; siehe
// docs/project-prototype.md.
export function ProjectPage() {
  const { projectId } = useParams();
  return projectId ? <ProjectDashboard kind="PROJECT" /> : <ProjectCreate />;
}

function ProjectCreate() {
  const { active: activeCompany } = useCompanyContext();
  const actingAsCompanyId = activeCompany?.canAdminister ? activeCompany.id : null;
  const intl = useIntl();
  const navigate = useNavigate();
  const validationFallback = useValidationFallback();
  const [createProject, { loading: creating }] = useMutation(CreateProjectDocument);
  const [error, setError] = useState<string | null>(null);

  return (
    <DashboardCreatePage
      kind="PROJECT"
      fields={actingAsCompanyId && activeCompany ? (
        <div id="project-create-actingas" className="mb-6 text-smycles-label text-ink">
          <FormattedMessage id="project.create.actingAsCompany"
            defaultMessage="Du legst das Projekt als {company} an. Verlasse den Unternehmenskontext, um es als du selbst anzulegen."
            values={{ company: <span className="font-medium">{activeCompany.name}</span> }} />
        </div>
      ) : undefined}
      saving={creating}
      error={error}
      onCreate={async (created) => {
        setError(null);
        try {
          const response = await createProject({
            variables: {
              input: { data: { name: created.title, description: created.description, data: { status: "IDEA" }, ...(actingAsCompanyId ? { company: { id: actingAsCompanyId } } : {}) }, ...actingAsDeclaration(actingAsCompanyId) },
            },
          });
          const payload = response.data?.createProject;
          throwIfValidationRejected(payload, "ValidateProjectResult", validationFallback);
          const saved = payload && "result" in payload ? payload.result : null;
          // Angelegt heißt: ab jetzt hat das Projekt seine Adresse. `replace`, damit der Zurück-Knopf
          // nicht in eine leere Maske führt, deren zweites Absenden ein zweites Projekt wäre; ein
          // Reiter aus der Maske (`?tab=`) überlebt das Anlegen nicht.
          if (saved) navigate(`/project/${saved.id}`, { replace: true });
        } catch (e) {
          // Nie die rohe Servermeldung zeigen (@adr SPARK-0010-no-raw-server-errors-in-ui@1).
          setError(projectWriteErrorMessage(intl, e, actingAsCompanyId));
        }
      }}
    />
  );
}
